import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@tSurya/Overrides/Widgets/Cart/CartLineQuantity";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY, COLORS } from "@tSurya/theme/design.surya";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import BrandBadge from "@tSurya/Components/BrandBadge";
import { useVisibleMgbwSelections } from "@tSurya/Common/Hooks/useMgbwSelections";
import { MGBW_CONFIGURATION_ITEM_NUMBER } from "@tSurya/Common/MgbwPropertyKeys";
import CartLineWhiteGloveService from "@tSurya/Components/CartLineWhiteGloveService";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    editable: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
});

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineCardExpandedStyles {
    cartLineNotes?: CartLineNotesStyles;
    price?: ProductPriceStyles;
    quantity?: CartLineQuantityStyles;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    productDescriptionAndPartNumbersGridItem?: GridItemProps;
    productDescriptionAndPartNumbersContainer?: GridContainerProps;
    productBrand?: ProductBrandStyles;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineNotesGridItem?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeValueText?: TypographyPresentationProps;
    costCodeLabelText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionNameText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    qtyHeadingAndText?: SmallHeadingAndTextStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    subtotalWithoutVatText?: SmallHeadingAndTextStyles;
    vatAmount?: SmallHeadingAndTextStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    cartLineServiceGridItem?: GridItemProps;
    serviceLabel?: InjectableCss;
    columnContainer?: InjectableCss;
    imageColumn?: InjectableCss;
    productColumn?: InjectableCss;
    badge?: InjectableCss;
    unitPriceColumn?: InjectableCss;
    qtyColumn?: InjectableCss;
    totalPriceColumn?: InjectableCss;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
    productDetails?: InjectableCss;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 0,
        css: css`
            margin-bottom: 0;
            padding: 0;
            position: relative;
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        `,
    },
    columnContainer: {
        css: css`
            display: grid;
            grid-template-columns: 115px 32% 1fr 1fr 1fr;
            gap: 16px;
            width: 100%;
            padding: 16px 0;
            align-items: flex-start;
            border-bottom: 1px solid #dddddd;
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;

            [data-test-selector="heading"] {
                display: none;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            grid-template-columns: 62px 1fr 1fr 1fr !important;
                            gap: 12px !important;

                            [data-test-selector="heading"] {
                                display: block !important;
                            }
                        `,
                        css`
                            grid-template-columns: 62px 32% 1fr 1fr 1fr;
                            gap: 12px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    imageColumn: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            grid-row-start: 1;
                            grid-column-start: 1;
                            grid-column-end: 2;
                            grid-row-end: 5;
                        `,
                    ],
                    "max",
                )}

            img {
                width: 115px;
                max-width: 115px;
                flex-shrink: 0;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                width: 62px !important;
                                max-width: 62px !important;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    badge: {
        css: css`
            margin-top: 8px;
            display: flex;
            align-items: center;
            justify-content: flex-start;
        `,
    },
    unitPriceColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: left;
            font-family: ${TYPOGRAPHY.fontFamily.base};

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            grid-row-start: 3;
                            grid-column-start: 3;
                            grid-column-end: 4;
                        `,
                    ],
                    "max",
                )}

            legend {
                display: none;
                text-transform: capitalize !important;
                font-weight: 400;
                font-family: ${TYPOGRAPHY.fontFamily.base};
                padding-left: 0;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                display: block;
                                font-size: ${TYPOGRAPHY.size.smaller};
                                color: ${COLORS.checkout.text.secondary};
                            `,
                        ],
                        "max",
                    )}
            }

            [class*="InventoryMessage"] {
                span {
                    font-weight: 500;
                }
            }

            span {
                font-weight: 600;
                font-family: ${TYPOGRAPHY.fontFamily.base};
            }
        `,
    },
    qtyColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            justify-content: center;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            grid-row-start: 3;
                            grid-column-start: 2;
                            grid-column-end: 3;
                            align-items: center;
                            text-align: center;
                            justify-content: center;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    totalPriceColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            text-align: right;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            grid-row-start: 3;
                            grid-column-start: 4;
                            grid-column-end: 5;
                            flex-direction: flex-end;
                            align-items: flex-end;
                            text-align: right;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    productColumn: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-direction: column;
            gap: 15px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            grid-row-start: 1;
                            grid-column-start: 2;
                            grid-column-end: 5;
                            gap: 10px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    productDetails: {
        css: css`
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-left: 2px;
                            padding-right: 30px;
                        `,
                    ],
                    "max",
                )}

            & a {
                color: ${COLORS.text.main} !important;
                text-decoration: none;
                font-weight: 700;

                span {
                    color: ${COLORS.text.main} !important;
                    text-decoration: none;
                    font-weight: 700;
                }

                &:hover {
                    color: ${COLORS.text.main} !important;
                    text-decoration: underline;
                    font-weight: 700;

                    span {
                        color: ${COLORS.text.main} !important;
                        text-decoration: none;
                        font-weight: 700;
                    }
                }
            }
        `,
    },
    cartLineServiceGridItem: { width: 12 },
    serviceLabel: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: italic;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: #545454;
        `,
    },
    productImageGridItem: {
        width: 2,
        printWidth: 1,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 0;
            }
        `,
    },
    orderLineInfoGridItem: {
        width: 10,
        printWidth: 11,
        css: css`
            display: none;
        `,
    },
    orderLineInfoContainer: {
        gap: 15,
    },
    productDescriptionAndPartNumbersGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 6,
    },
    productDescriptionAndPartNumbersContainer: {
        gap: 15,
    },
    productBrand: {
        nameText: {
            color: "text.main",
        },
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    configurationOptionText: {
        css: css`
            display: block;
        `,
    },
    productPartNumbersGridItem: {
        width: 12,
    },
    orderLineNotesGridItem: {
        width: 12,
    },
    costCodeGridItem: { width: 12 },
    costCodeLabelText: {
        weight: "bold",
        css: css`
            margin-right: 10px;
        `,
    },
    productPriceAndQuantityGridItem: {
        width: [12, 12, 7, 7, 7],
        printWidth: 6,
    },
    productPriceAndQuantityContainer: {
        gap: 15,
    },
    priceGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    quantityGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    extendedUnitNetPriceGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-flow: wrap;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    price: {
        wrapper: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                align-items: center;
                                text-align: center;
                            `,
                        ],
                        "max",
                    )}
            `,
        }
    },
    extendedUnitNetPrice: {
        wrapper: {
            css: css`
                align-items: flex-end;
                text-align: right;
            `,
        },
        text: {
            weight: 700,
        },
        heading: {
            css: css`
                text-transform: none !important;
                display: none !important;
                font-size: ${TYPOGRAPHY.size.smaller} !important;
            `,
        },
    },
    subtotalWithoutVatText: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
                width: 100%;
                color: #545454;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
        extendedStyles: {
            heading: {
                css: css`
                    display: none !important;
                `,
            },
        },
    },
    vatAmount: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
                width: 100%;
                color: #545454;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
        extendedStyles: {
            heading: {
                css: css`
                    display: none !important;
                `,
            },
        },
    },
    cartLineErrorMessageGridItem: {
        width: 12,
    },
    cartLineErrorMessageText: {
        color: "danger",
    },
    quantity: {
        readOnlyQuantityText: {
            extendedStyles: {
                wrapper: {
                    css: css`
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        align-items: flex-start;
                                        text-align: left;
                                    `,
                                ],
                                "max",
                            )}
                    `,
                },
                heading: {
                    css: css`
                        font-family: inherit;
                        font-weight: 400;
                        text-transform: none;
                        color: ${COLORS.text.main};
                        font-size: ${TYPOGRAPHY.size.smaller};
                    `,
                },
            },
        },
    },
};

const CartLineCardExpanded = ({
    cart,
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    promotions,
    editable,
    enableVat,
    vatPriceDisplay,
    showInvalidPriceMessage,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    const sideMarks = cartLine.notes?.trim();

    const sourceBrandNormalized = (cartLine?.properties?.sourceBrand ?? "").toLowerCase().replace(/\s+/g, "");

    const mgbwVisibleSelections = useVisibleMgbwSelections(cartLine.properties);
    const configurationItemNumber = cartLine.properties?.[MGBW_CONFIGURATION_ITEM_NUMBER] || "";
    const isMgbwConfigured = mgbwVisibleSelections.length > 0 || !!configurationItemNumber;
    const isWhiteGloveLine = cartLine.properties.isWhiteGlove === "true";

    const whiteGloveServiceLabel =
        isWhiteGloveLine && isCheckoutReviewAndSubmitPage ? (
            <CartLineWhiteGloveService />
        ) : (
            <StyledWrapper {...styles.serviceLabel}>White Glove Service</StyledWrapper>
        );

    return (
        <GridContainer {...styles.container} data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}>
            <StyledWrapper {...styles.columnContainer}>
                <StyledWrapper {...styles.imageColumn}>
                    <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                    <StyledWrapper {...styles.badge}>
                        <BrandBadge brandName={cartLine?.properties?.sourceBrand} />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.productColumn}>
                    <StyledWrapper {...styles.productDetails}>
                        {cartLine.brand && sourceBrandNormalized === "surya" && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />}
                        <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                    </StyledWrapper>
                    {isMgbwConfigured ? (
                        <StyledWrapper>
                            {mgbwVisibleSelections.map(entry => (
                                <Typography {...styles.configurationOptionText} key={entry.OptionId}>
                                    {`${entry.Name}: ${entry.Value}`}
                                </Typography>
                            ))}
                        </StyledWrapper>
                    ) : (
                        <>
                            {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                                <StyledWrapper>
                                    {cartLine.sectionOptions!.map(option => (
                                        <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                            {`${option.sectionName}:${option.optionName}`}
                                        </Typography>
                                    ))}
                                </StyledWrapper>
                            )}
                            <ProductPartNumbers
                                productNumber={cartLine.erpNumber}
                                customerProductNumber={cartLine.customerName}
                                manufacturerItem={cartLine.manufacturerItem}
                                extendedStyles={styles.productPartNumbers}
                            />
                        </>
                    )}
                    {sideMarks && (
                        <StyledWrapper>
                            <CartLineNotes cart={cart} editable={false} label={translate("Side Mark")} extendedStyles={styles.cartLineNotes} />
                        </StyledWrapper>
                    )}
                    {cartLine.isRestricted && (
                        <StyledWrapper>
                            <Typography {...styles.cartLineErrorMessageText} data-test-selector="cartline_restrictedMessage">
                                {translate("Restricted product")}
                            </Typography>
                        </StyledWrapper>
                    )}
                    {!cartLine.isActive && (
                        <StyledWrapper>
                            <Typography {...styles.cartLineErrorMessageText}>{translate("Inactive Product")}</Typography>
                        </StyledWrapper>
                    )}
                    {/* Roll Pad Service Label */}
                    {cartLine.properties.isRollpadService === "true" && <StyledWrapper {...styles.serviceLabel}>Roll Pad Service</StyledWrapper>}
                    {cartLine.properties.isWhiteGlove === "true" && whiteGloveServiceLabel}
                </StyledWrapper>
                <StyledWrapper {...styles.unitPriceColumn}>
                    <ProductPrice
                        product={cartLine}
                        showSavings={true}
                        showSavingsAmount={showSavingsAmount}
                        showSavingsPercent={showSavingsPercent}
                        showInvalidPriceMessage={showInvalidPriceMessage}
                        extendedStyles={styles.price}
                    />
                    {promotions?.map(promotion => (
                        <Typography {...styles.promotionNameText} key={promotion.id} data-test-selector="orderLineCardExpandedPromotionName">
                            {promotion.name}
                        </Typography>
                    ))}
                </StyledWrapper>
                <StyledWrapper {...styles.qtyColumn}>
                    <CartLineQuantity cart={cart} editable={editable} extendedStyles={styles.quantity} />
                </StyledWrapper>
                <StyledWrapper {...styles.totalPriceColumn}>
                    {cartLine.pricing && (showPricing || isCheckoutReviewAndSubmitPage) && (
                        <>
                            {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                <>
                                    <SmallHeadingAndText
                                        heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                        text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                        extendedStyles={styles.subtotalWithoutVatText}
                                    />
                                    <SmallHeadingAndText
                                        heading={`${translate("Total VAT")} (${cartLine.pricing.vatRate}%)`}
                                        text={cartLine.pricing.vatAmountDisplay}
                                        extendedStyles={styles.vatAmount}
                                    />
                                </>
                            )}
                            <SmallHeadingAndText
                                heading={
                                    !enableVat
                                        ? translate("Total Price")
                                        : vatPriceDisplay !== "DisplayWithoutVat"
                                          ? `${translate("Total Price")} (${translate("Inc. VAT")})`
                                          : `${translate("Total Price")} (${translate("Ex. VAT")})`
                                }
                                text={
                                    enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                        : cartLine.pricing.extendedUnitNetPriceDisplay
                                }
                                extendedStyles={styles.extendedUnitNetPrice}
                            />
                        </>
                    )}
                </StyledWrapper>
                <GridItem {...styles.orderLineInfoGridItem}>
                    <GridContainer {...styles.orderLineInfoContainer}>
                        <GridItem {...styles.productDescriptionAndPartNumbersGridItem}>
                            <GridContainer {...styles.productDescriptionAndPartNumbersContainer}>
                                {/* <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                    {cartLine.brand && (
                                        <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                                    )}
                                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                                </GridItem> */}
                                {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                                    <GridItem {...styles.configurationGridItem}>
                                        {cartLine.sectionOptions!.map(option => (
                                            <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                                {`${option.sectionName}:${option.optionName}`}
                                            </Typography>
                                        ))}
                                    </GridItem>
                                )}
                                <GridItem {...styles.productPartNumbersGridItem}>
                                    <ProductPartNumbers
                                        productNumber={cartLine.erpNumber}
                                        customerProductNumber={cartLine.customerName}
                                        manufacturerItem={cartLine.manufacturerItem}
                                        extendedStyles={styles.productPartNumbers}
                                    />
                                </GridItem>
                                {cartLine.isRestricted && (
                                    <GridItem {...styles.cartLineErrorMessageGridItem}>
                                        <Typography {...styles.cartLineErrorMessageText} data-test-selector="cartline_restrictedMessage">
                                            {translate("Restricted product")}
                                        </Typography>
                                    </GridItem>
                                )}
                                {!cartLine.isActive && (
                                    <GridItem {...styles.cartLineErrorMessageGridItem}>
                                        <Typography {...styles.cartLineErrorMessageText}>{translate("Inactive Product")}</Typography>
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.productPriceAndQuantityGridItem}>
                            <GridContainer {...styles.productPriceAndQuantityContainer}>
                                <GridItem {...styles.priceGridItem}>
                                    <ProductPrice
                                        product={cartLine}
                                        showSavings={true}
                                        showSavingsAmount={showSavingsAmount}
                                        showSavingsPercent={showSavingsPercent}
                                        showInvalidPriceMessage={showInvalidPriceMessage}
                                        extendedStyles={styles.price}
                                    />
                                    {promotions?.map(promotion => (
                                        <Typography {...styles.promotionNameText} key={promotion.id} data-test-selector="orderLineCardExpandedPromotionName">
                                            {promotion.name}
                                        </Typography>
                                    ))}
                                </GridItem>
                                <GridItem {...styles.quantityGridItem} data-test-selector="orderConformation_Quantity">
                                    <CartLineQuantity cart={cart} editable={editable} extendedStyles={styles.quantity} />
                                </GridItem>
                                <GridItem {...styles.extendedUnitNetPriceGridItem}>
                                    {cartLine.pricing && (showPricing || isCheckoutReviewAndSubmitPage) && (
                                        <>
                                            {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                <>
                                                    <SmallHeadingAndText
                                                        heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                                        text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                                        extendedStyles={styles.subtotalWithoutVatText}
                                                    />
                                                    <SmallHeadingAndText
                                                        heading={`${translate("Total VAT")} (${cartLine.pricing.vatRate}%)`}
                                                        text={cartLine.pricing.vatAmountDisplay}
                                                        extendedStyles={styles.vatAmount}
                                                    />
                                                </>
                                            )}
                                            <SmallHeadingAndText
                                                heading={
                                                    !enableVat
                                                        ? translate("Total Price")
                                                        : vatPriceDisplay !== "DisplayWithoutVat"
                                                          ? `${translate("Total Price")} (${translate("Inc. VAT")})`
                                                          : `${translate("Total Price")} (${translate("Ex. VAT")})`
                                                }
                                                text={
                                                    enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                        : cartLine.pricing.extendedUnitNetPriceDisplay
                                                }
                                                extendedStyles={styles.extendedUnitNetPrice}
                                            />
                                        </>
                                    )}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        {cartLine.properties.isRollpadService === "true" && (
                            <GridItem
                                {...styles.cartLineServiceGridItem}
                                data-test-selector="orderConformation_CostCode"
                            >
                                <StyledWrapper {...styles.serviceLabel}>Roll Pad Service</StyledWrapper>
                            </GridItem>
                        )}
                        {cartLine.properties.isWhiteGlove === "true" && <GridItem {...styles.cartLineServiceGridItem}>{whiteGloveServiceLabel}</GridItem>}
                        {cart.showCostCode && cartLine.costCode && (
                            <GridItem {...styles.costCodeGridItem}>
                                <Typography {...styles.costCodeValueText} as="p">
                                    <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}:</Typography>
                                    {cartLine.costCode}
                                </Typography>
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            </StyledWrapper>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineCardExpanded));

